/**
 * Restores focus inside the cart drawer after a quantity change (WCAG 2.4.3).
 * `theme.js` rebuilds the whole line-items list wholesale on every
 * `theme:cart:change` (`itemsHolder.innerHTML = ...`), which destroys the
 * focused quantity control and can drop focus to <body> even though the
 * drawer stays open and aria-modal. The existing focus trap only recovers
 * focus that lands back on itself, not on <body>, so a lost focus is
 * otherwise unrecoverable except by tabbing through the whole page.
 *
 * The rebuilt DOM arrives via a second, unbounded fetch after
 * `theme:cart:change` fires, so restoration waits for a MutationObserver on
 * the items holder (bounded by a timeout) rather than a fixed delay.
 */
(function () {
  'use strict';

  var DRAWER_SELECTOR = '#cart-drawer';
  var ITEMS_HOLDER_SELECTOR = '[data-items-holder]';
  var QUANTITY_CONTROL_SELECTOR = '[data-quantity-minus], [data-quantity-plus], [data-quantity-field]';
  var FOCUSABLE_SELECTOR =
    'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
  var SETTLE_TIMEOUT = 2000;

  var pending = null; // { key, control: 'minus' | 'plus' | 'field' }

  function controlType(el) {
    if (el.hasAttribute('data-quantity-minus')) return 'minus';
    if (el.hasAttribute('data-quantity-plus')) return 'plus';
    if (el.hasAttribute('data-quantity-field')) return 'field';
    return null;
  }

  function recordFromEvent(e) {
    var drawer = document.querySelector(DRAWER_SELECTOR);
    if (!drawer || !drawer.contains(e.target)) return;
    var control = e.target.closest && e.target.closest(QUANTITY_CONTROL_SELECTOR);
    if (!control) return;
    var type = controlType(control);
    var holder = control.closest('[data-quantity-holder]');
    var field = holder && holder.querySelector('[data-quantity-field]');
    var key = field && field.getAttribute('data-id');
    if (!key || !type) return;
    pending = { key: key, control: type };
  }

  ['pointerdown', 'keydown', 'click'].forEach(function (type) {
    document.addEventListener(type, recordFromEvent, true);
  });

  function isDrawerOpen(drawer) {
    return !!drawer && drawer.classList.contains('is-open');
  }

  function focusLost(drawer) {
    var active = document.activeElement;
    return !active || active === document.body || !drawer.contains(active);
  }

  function firstFocusable(container) {
    if (!container) return null;
    var candidates = container.querySelectorAll(FOCUSABLE_SELECTOR);
    for (var i = 0; i < candidates.length; i++) {
      var el = candidates[i];
      if (el.offsetWidth || el.offsetHeight || el.getClientRects().length) return el;
    }
    return null;
  }

  function restoreFocus() {
    var descriptor = pending;
    pending = null;
    if (!descriptor) return;

    var drawer = document.querySelector(DRAWER_SELECTOR);
    if (!isDrawerOpen(drawer) || !focusLost(drawer)) return;

    var field = drawer.querySelector('[data-quantity-field][data-id="' + descriptor.key + '"]');
    var holder = field && field.closest('[data-quantity-holder]');

    var target = null;
    if (holder) {
      if (descriptor.control === 'minus') target = holder.querySelector('[data-quantity-minus]');
      else if (descriptor.control === 'plus') target = holder.querySelector('[data-quantity-plus]');
      else target = field;
    }

    if (!target) {
      target = drawer.querySelector('[data-item-remove][data-id="' + descriptor.key + '"]');
    }
    if (!target) {
      target = drawer.querySelector('[data-cart-toggle]') || firstFocusable(drawer);
    }
    if (!target) {
      target = drawer;
    }

    target.focus();
  }

  function waitForSettleAndRestore() {
    var itemsHolder = document.querySelector(ITEMS_HOLDER_SELECTOR);
    if (!itemsHolder) {
      restoreFocus();
      return;
    }

    var done = false;
    var timeoutId;

    var observer = new MutationObserver(function () {
      if (done) return;
      done = true;
      window.clearTimeout(timeoutId);
      observer.disconnect();
      restoreFocus();
    });

    observer.observe(itemsHolder, { childList: true, subtree: true });

    timeoutId = window.setTimeout(function () {
      if (done) return;
      done = true;
      observer.disconnect();
      restoreFocus();
    }, SETTLE_TIMEOUT);
  }

  document.addEventListener('theme:cart:change', function () {
    if (!pending) return;
    waitForSettleAndRestore();
  });

  document.addEventListener('theme:drawer:close', function () {
    pending = null;
  });
  document.addEventListener('theme:cart:close', function () {
    pending = null;
  });
})();
